import { createPortal } from 'react-dom';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react';

export type ToastTone = 'info' | 'success' | 'warning' | 'error';

export interface ToastOptions {
  readonly tone?: ToastTone;
  readonly title?: string;
  readonly durationMs?: number;
  readonly action?: { readonly label: string; readonly onClick: () => void };
}

interface ToastItem extends ToastOptions {
  readonly id: number;
  readonly message: string;
  readonly tone: ToastTone;
}

interface ToastApi {
  readonly notify: (message: string, options?: ToastOptions) => number;
  readonly dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const toneIcons: Record<ToastTone, string> = { info: 'info', success: 'check_circle', warning: 'warning', error: 'error' };

let nextId = 1;

export function ToastProvider({ children }: PropsWithChildren): React.JSX.Element {
  const [toasts, setToasts] = useState<readonly ToastItem[]>([]);
  const timers = useRef(new Map<number, number>());

  const dismiss = useCallback((id: number): void => {
    const timer = timers.current.get(id);
    if (timer) window.clearTimeout(timer);
    timers.current.delete(id);
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const notify = useCallback((message: string, options: ToastOptions = {}): number => {
    const id = nextId++;
    const tone = options.tone ?? 'info';
    const duration = options.durationMs ?? (tone === 'error' ? 7_000 : 4_200);
    setToasts((current) => [...current.slice(-3), { ...options, id, message, tone }]);
    timers.current.set(id, window.setTimeout(() => dismiss(id), duration));
    return id;
  }, [dismiss]);

  useEffect(() => {
    // Qualquer tela pode avisar o usuário sem depender do contexto React
    // (repositórios, service worker, listeners globais).
    const receive = (event: Event): void => {
      const detail = (event as CustomEvent<{ message: string } & ToastOptions>).detail;
      if (detail?.message) notify(detail.message, detail);
    };
    window.addEventListener('echo-toast', receive);
    const pending = timers.current;
    return () => {
      window.removeEventListener('echo-toast', receive);
      pending.forEach((timer) => window.clearTimeout(timer));
    };
  }, [notify]);

  const value = useMemo<ToastApi>(() => ({ notify, dismiss }), [dismiss, notify]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(<div className="toast-region" aria-live="polite" aria-relevant="additions">
        {toasts.map((toast) => (
          <div className={`toast toast-${toast.tone}`} key={toast.id} role={toast.tone === 'error' ? 'alert' : 'status'}>
            <span className="material-symbols-outlined toast-icon" aria-hidden="true">{toneIcons[toast.tone]}</span>
            <div className="toast-body">
              {toast.title && <strong>{toast.title}</strong>}
              <p>{toast.message}</p>
            </div>
            {toast.action && <button className="toast-action" type="button" onClick={() => { toast.action?.onClick(); dismiss(toast.id); }}>{toast.action.label}</button>}
            <button className="toast-close" type="button" aria-label="Fechar aviso" onClick={() => dismiss(toast.id)}><span className="material-symbols-outlined" aria-hidden="true">close</span></button>
          </div>
        ))}
      </div>, document.body)}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast deve ser usado dentro de ToastProvider.');
  return context;
}

export function toast(message: string, options?: ToastOptions): void {
  window.dispatchEvent(new CustomEvent('echo-toast', { detail: { message, ...options } }));
}
